import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'

import {
  PrepTimeIcon,
  ScheduleIcon,
  WarningOutlineIcon
} from '@toasttab/buffet-pui-icons'
import classnames from 'classnames'
import parseISO from 'date-fns/parseISO'

import {
  DiningOptionBehavior,
  FulfillmentType
} from 'src/apollo/onlineOrdering'
import { ButtonType } from 'src/apollo/sites'
import { reportError } from 'src/lib/js/clientError'
import { BrandedButton } from 'src/shared/components/common/button'
import {
  Modal,
  ModalContent,
  ModalOverlay
} from 'src/shared/components/common/modal'

import { ToggleInput } from 'shared/components/common/forms'
import LoadingSpinnerOverlay from 'shared/components/common/loading_spinner/LoadingSpinnerOverlay'
import { alertError } from 'shared/js/alertUtils'
import { REQUEST_FAILURE_MESSAGE } from 'shared/js/constants'

import AnimatedSection from 'public/components/default_template/online_ordering/checkout/AnimatedSection'
import FulfillmentTimeDropdowns from 'public/components/default_template/online_ordering/dining_behavior_toggle/FulfillmentTimeDropdowns'
import { TimeBasedRuleMenuItemTag } from 'public/components/default_template/online_ordering/item_tags/MenuItemTags'
import { useCart } from 'public/components/online_ordering/CartContext'
import {
  getDateTimeIndexes,
  useFulfillment
} from 'public/components/online_ordering/FulfillmentContext'
import {
  ItemWithTimeBasedRule,
  useTimeBasedRules
} from 'public/components/online_ordering/TimeBasedRuleContext'
import {
  getTrackingSourceFromPath,
  trackDiningOptionChangedV2
} from 'src/public/components/default_template/online_ordering/dining_behavior_toggle/fulfillmentTrackingUtils'
import { useRestaurantRoutes } from 'src/shared/components/common/restaurant_routes/RestaurantRoutesContext'
import useTracker from 'src/lib/js/hooks/useTracker'
import { t } from 'src/translations'

const FulfillmentTimeModal = ({
  returnFocusElementId
}: {
  returnFocusElementId?: string
}) => {
  const {
    selectedFulfillmentType,
    setSelectedFulfillmentType,
    updateCartFulfillment,
    setGuestSelectedFulfillmentTime,
    fulfillmentData,
    fulfillmentTimeModalOpen,
    setFulfillmentTimeModalOpen,
    selectedDiningOptionBehavior,
    diningOptionBehaviorAvailability
  } = useFulfillment()
  const { deleteFromCart } = useCart()
  const {
    itemInCartWithMaxLeadTime,
    itemsInCartWithPreorderRule,
    itemsInCartWithPickupWindowRule,
    selectionsInCartWithTBRs,
    getEarliestFulfillmentTime,
    validateItemFulfillmentTime,
    timeBasedRulesMap
  } = useTimeBasedRules()
  const asapAvailable = useMemo(() => {
    const behavior =
      selectedDiningOptionBehavior === DiningOptionBehavior.TakeOut
        ? diningOptionBehaviorAvailability.takeout
        : diningOptionBehaviorAvailability.delivery
    return Boolean(
      behavior?.asapAvailable && selectionsInCartWithTBRs.length === 0
    )
  }, [
    selectedDiningOptionBehavior,
    diningOptionBehaviorAvailability,
    selectionsInCartWithTBRs.length
  ])
  const defaultFulfillmentType = useMemo(
    () =>
      !asapAvailable
        ? FulfillmentType.Future
        : selectedFulfillmentType ?? FulfillmentType.Asap,
    [asapAvailable, selectedFulfillmentType]
  )
  const [draftFulfillmentTime, setDraftFulfillmentTime] = useState<
    string | null | undefined
  >()
  const [draftFulfillmentType, setDraftFulfillmentType] =
    useState<FulfillmentType>(defaultFulfillmentType)
  const [submitting, setSubmitting] = useState(false)
  const [warning, setWarning] = useState<string | undefined>()
  const [itemToRemove, setItemToRemove] = useState<ItemWithTimeBasedRule>()
  const tracker = useTracker()
  const { checkoutPath, orderPath } = useRestaurantRoutes()

  useEffect(() => {
    setDraftFulfillmentTime(
      fulfillmentData?.cartFulfillmentData?.fulfillmentDateTime
    )
    setDraftFulfillmentType(defaultFulfillmentType)
  }, [
    fulfillmentData?.cartFulfillmentData?.fulfillmentDateTime,
    defaultFulfillmentType
  ])

  useEffect(() => {
    const [dayIndex, timeIndex] = getDateTimeIndexes(
      fulfillmentData?.cartFulfillmentData?.fulfillmentDateTime,
      fulfillmentData?.scheduleData?.futureScheduleDates
    )
    const expired =
      dayIndex != null && timeIndex != null && (dayIndex < 0 || timeIndex < 0)
    if (expired) {
      setWarning(t('fulfillment-time-modal.warning.your-order'))
      setFulfillmentTimeModalOpen(true)
      return
    }

    // The last item in the list should be the most recently added one, which is the one we need to validate
    const preorderInvalid = !validateItemFulfillmentTime(
      itemsInCartWithPreorderRule[itemsInCartWithPreorderRule.length - 1]
        ?.itemGuid
    )
    const pickupWindowInvalid = !validateItemFulfillmentTime(
      itemsInCartWithPickupWindowRule[
        itemsInCartWithPickupWindowRule.length - 1
      ]?.itemGuid
    )

    if (itemInCartWithMaxLeadTime || preorderInvalid || pickupWindowInvalid) {
      if (
        itemInCartWithMaxLeadTime &&
        !preorderInvalid &&
        !pickupWindowInvalid
      ) {
        const fulfillmentTime =
          fulfillmentData?.cartFulfillmentData.fulfillmentDateTime
        if (
          fulfillmentTime &&
          parseISO(fulfillmentTime) >
            getEarliestFulfillmentTime(
              itemInCartWithMaxLeadTime?.itemGuid,
              selectedDiningOptionBehavior
            )
        ) {
          // If the chosen fulfillment time is still later than the earliest possible fulfillment time for the current cart, we don't need to open the modal
          return
        }
      }
      setWarning(
        selectedDiningOptionBehavior === DiningOptionBehavior.Delivery
          ? t('fulfillment-time-modal.warning.update-delivery')
          : t('fulfillment-time-modal.warning.update-pickup')
      )
      setFulfillmentTimeModalOpen(true)
      const lastSelection =
        selectionsInCartWithTBRs[selectionsInCartWithTBRs.length - 1]
      if (lastSelection?.itemGuid) {
        const rules = timeBasedRulesMap[lastSelection.itemGuid]
        setItemToRemove({
          itemGuid: lastSelection.itemGuid,
          selectionGuid: lastSelection.guid,
          name: lastSelection.name,
          leadTimeRule: rules?.leadTimeRule || undefined,
          preorderRule: rules?.preorderRule || undefined,
          pickupWindowRule: rules?.pickupWindowRule || undefined
        })
      }
      return
    }

    setWarning(undefined)
    setItemToRemove(undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fulfillmentData,
    selectedDiningOptionBehavior,
    selectionsInCartWithTBRs,
    itemInCartWithMaxLeadTime,
    itemsInCartWithPreorderRule,
    itemsInCartWithPickupWindowRule
  ])

  const handleCancel = useCallback(async () => {
    setDraftFulfillmentType(defaultFulfillmentType)
    setDraftFulfillmentTime(undefined)
    await setFulfillmentTimeModalOpen(false)
  }, [setFulfillmentTimeModalOpen, defaultFulfillmentType])

  const handleRemoveItem = useCallback(async () => {
    setWarning(undefined)
    if (itemToRemove?.selectionGuid) {
      deleteFromCart(itemToRemove.selectionGuid)
    }
    setItemToRemove(undefined)
    await setFulfillmentTimeModalOpen(false)
  }, [deleteFromCart, setFulfillmentTimeModalOpen, itemToRemove])

  const handleSubmit = useCallback(async () => {
    let success
    setSubmitting(true)
    try {
      const diningOptionBehavior =
        fulfillmentData?.cartFulfillmentData?.diningOptionBehavior

      success = await updateCartFulfillment({
        fulfillmentType: draftFulfillmentType,
        fulfillmentDateTime: draftFulfillmentTime,
        diningOptionBehavior,
        deliveryInfo:
          diningOptionBehavior === DiningOptionBehavior.Delivery
            ? fulfillmentData?.cartFulfillmentData?.deliveryInfo
            : undefined
      })
      setSelectedFulfillmentType(draftFulfillmentType)
      trackDiningOptionChangedV2(
        tracker,
        draftFulfillmentType,
        false,
        getTrackingSourceFromPath(
          window.location.pathname,
          checkoutPath,
          orderPath
        ),
        fulfillmentData?.cartFulfillmentData.diningOptionBehavior,
        fulfillmentData?.cartFulfillmentData.deliveryInfo
      )
    } catch (error) {
      success = false
      alertError(REQUEST_FAILURE_MESSAGE)
      reportError('Error changing cart fulfillment.', error)
    }
    setSubmitting(false)

    if (success) {
      setFulfillmentTimeModalOpen(false)
      setGuestSelectedFulfillmentTime(true)
      setWarning(undefined)
      setItemToRemove(undefined)
    }
  }, [
    updateCartFulfillment,
    draftFulfillmentType,
    draftFulfillmentTime,
    fulfillmentData,
    setSelectedFulfillmentType,
    tracker,
    checkoutPath,
    orderPath,
    setFulfillmentTimeModalOpen,
    setGuestSelectedFulfillmentTime
  ])

  return (
    <Modal
      isOpen={fulfillmentTimeModalOpen}
      onClose={() => setFulfillmentTimeModalOpen(false)}
      preventOverlayClose
      testId='fulfillment-time-modal'
    >
      <ModalOverlay fadeIn />
      <ModalContent
        contentClassName='modalContentV2 fullScreenMobile fulfillmentModal fulfillmentTimeModal'
        returnFocusElementId={returnFocusElementId}
        ariaLabelledBy='fulfillment-time-modal-heading'
      >
        <div className='modalHeader'>
          <h2 id='fulfillment-time-modal-heading'>
            {warning
              ? t('fulfillment-time-modal.update-order')
              : t('fulfillment-time-modal.order-timing')}
          </h2>
        </div>
        {warning && (
          <div className='warningBanner'>
            <WarningOutlineIcon size='xs' accessibility='decorative' />
            <span>{warning}</span>
          </div>
        )}
        {itemToRemove && (
          <div className='tbrItem'>
            <div>{itemToRemove.name}</div>
            <TimeBasedRuleMenuItemTag
              timeBasedRules={{
                leadTimeRule: itemToRemove.leadTimeRule,
                pickupWindowRule: itemToRemove.pickupWindowRule,
                preorderRule: itemToRemove.preorderRule
              }}
              backgroundColor='#FFFFFF'
              tagColorOverride='rgb(0, 0, 0, 0.12)'
            />
          </div>
        )}
        <div
          className={classnames(
            'modalContent',
            asapAvailable ? 'asapAvailable' : undefined
          )}
        >
          <div
            className='radioButtons'
            role='radiogroup'
            aria-label={t('fulfillment-time-modal.order-timing')}
          >
            <FulfillmentTimeModalButton
              label={t('fulfillment-time-modal.asap')}
              icon={<PrepTimeIcon size='xs' accessibility='decorative' />}
              onChange={() => {
                setDraftFulfillmentType(FulfillmentType.Asap)
                setDraftFulfillmentTime(null)
              }}
              disabled={!asapAvailable}
              selected={draftFulfillmentType === FulfillmentType.Asap}
              id='asap'
            />
            <FulfillmentTimeModalButton
              label={t('fulfillment-time-modal.schedule')}
              icon={<ScheduleIcon size='xs' accessibility='decorative' />}
              onChange={() => setDraftFulfillmentType(FulfillmentType.Future)}
              selected={draftFulfillmentType === FulfillmentType.Future}
              id='scheduled'
            />
          </div>
          <AnimatedSection
            expanded={draftFulfillmentType === FulfillmentType.Future}
            className='fulfillmentTimeDropdownContainer'
          >
            <FulfillmentTimeDropdowns
              fulfillmentTime={draftFulfillmentTime}
              setFulfillmentTime={setDraftFulfillmentTime}
              draftFulfillmentType={draftFulfillmentType}
              fulfillmentTimeExpired={!!warning}
            />
          </AnimatedSection>
        </div>
        <div className='footer'>
          {!warning && (
            <BrandedButton
              variant={ButtonType.Secondary}
              onClick={handleCancel}
              className='cancelButton'
            >
              {t('fulfillment-time-modal.cancel')}
            </BrandedButton>
          )}
          {itemToRemove && (
            <BrandedButton
              variant={ButtonType.Secondary}
              onClick={handleRemoveItem}
              className='cancelButton'
            >
              {t('fulfillment-time-modal.remove-item')}
            </BrandedButton>
          )}
          <BrandedButton
            variant={ButtonType.Primary}
            onClick={handleSubmit}
            testId='fulfillment-time-modal-confirm'
          >
            {t('fulfillment-time-modal.confirm')}
          </BrandedButton>
        </div>
        {submitting && <LoadingSpinnerOverlay withSubtleBorderRadius />}
      </ModalContent>
    </Modal>
  )
}

const FulfillmentTimeModalButton = ({
  label,
  icon,
  selected,
  onChange,
  disabled,
  id
}: {
  label: string
  icon: ReactElement
  selected: boolean
  onChange: () => void
  disabled?: boolean
  id: string
}) => {
  return (
    <div
      className={classnames('radioButtonOption', disabled ? 'disabled' : '')}
    >
      {icon}
      <ToggleInput
        checked={selected}
        onChange={onChange}
        name='fulfillmentTime'
        id={id}
        type='radio'
        disabled={disabled}
        dataTestId={`input-location-${label}`}
      >
        <div>{label}</div>
      </ToggleInput>
    </div>
  )
}

export default FulfillmentTimeModal
